import Box from '@mui/material/Box';
import classNames from 'classnames';
import { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import useFeature from '~/store/clinic/hooks/useFeature';
import { useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import formatClientAddress from '~/utils/dataFormat/formatClientAddress';
import { isoToLocal } from '~/utils/date/date';
import useClientMultiTeamId from '~/hooks/useClientMultiTeamId';
import clientInfoIcon from '~/ui/assets/images/clientInfo.svg';
import Avatar from '~/ui/components/common/Avatar';
import { generalDateDotted } from '~/ui/constants/dateFormat';
import { VIEW_CLIENT } from '~/ui/constants/paths';
import { suicideRiskColor } from '~/ui/constants/suicideRisks';
import CeChip from '~/ui/pages/Clients/reusable/PersonalDetails/MedicaidPlan/components/CeChip';
import ExpansionChip from '~/ui/pages/Clients/reusable/PersonalDetails/MedicaidPlan/components/ExpansionChip';
import MedicaidTypeLabel from '~/ui/pages/Clients/reusable/PersonalDetails/MedicaidPlan/components/MedicaidTypeLabel';
import useActiveMedicaidPlan from '~/ui/pages/Clients/reusable/PersonalDetails/MedicaidPlan/hooks/useActiveMedicaidPlan';
import StatusHistoryItem from '~/ui/pages/Clients/reusable/Status/StatusHistoryItem';

import {
  getExpiresIn,
  getPaperworkStatus,
  getRecertificationColorByDate,
} from '~/ui/pages/Clients/reusable/PersonalDetails/helpers';
import getRiskAssessmentLabel from '~/ui/pages/MedicationLogistics/helpers/getRiskAssessmentLabel';

import styles from '../ClientInfo.module.scss';

interface IMainInfoProps {
  collapsed: boolean;
  onVisitsPage: boolean;
}

const MainInfo = ({ collapsed, onVisitsPage }: IMainInfoProps): ReactElement => {
  const { current } = useStoreState(state => state.client);
  const { isGlobalUser, clinic } = useRole();
  const { isMedicaidAvailable } = useFeature();

  const {
    photo,
    medicaId,
    address,
    primaryDiagnosis,
    firstName = '',
    lastName = '',
    suicideRisk,
    aot,
    riskAssessment,
    gender,
    dateOfBirth,
    medicaIdRecertificationDate,
    paperworkStatus,
    status,
    actTeams,
  } = current || {};

  const clientAddress = address ? formatClientAddress(address) : null;

  const formattedRecertification = medicaIdRecertificationDate
    ? isoToLocal(medicaIdRecertificationDate, 'MM/yyyy')
    : null;

  const color = suicideRiskColor[suicideRisk];

  const { getClientMultiTeamId } = useClientMultiTeamId();
  const clientTeamId = getClientMultiTeamId(current?.actTeams);

  const navigateToClientProfileProps = {
    to: VIEW_CLIENT.replace(':clientId', String(current?.id)).replace(
      ':actTeamId',
      String(clientTeamId),
    ),
    state: { actTeamId: String(clientTeamId) },
  };

  const clinicId = String(clinic?.id);
  const clientId = String(current?.id);
  const { plan: medicaidPlan } = useActiveMedicaidPlan({
    enabled: onVisitsPage && isMedicaidAvailable && !!current,
    clinicId,
    teamId: String(clientTeamId),
    clientId,
  });
  const isExpansion = !!medicaidPlan?.medicaidType?.isExpansion;

  const medicaIdColor = getRecertificationColorByDate(medicaIdRecertificationDate, paperworkStatus);

  return (
    <Box className={classNames(styles.row, styles.marginRight, styles.wide)}>
      {collapsed && (
        <>
          <img className={styles.marginRight} src={clientInfoIcon} alt="client info" />
          <Link {...navigateToClientProfileProps} className={classNames(styles.link, styles.red)}>
            <span className={styles.name}>{`${firstName} ${lastName}`}</span>
          </Link>
          {isExpansion && <ExpansionChip className={styles.collapsedExpansion} />}
        </>
      )}

      <Avatar url={photo} className={classNames(styles.avatar, { [styles.hidden]: collapsed })} />

      {!collapsed && current && (
        <Box className={styles.general}>
          <div style={{ color }} className={styles.name}>
            <Link {...navigateToClientProfileProps} className={styles.link}>
              <span
                className={classNames(styles.black, styles.marginRight)}
              >{`${firstName} ${lastName}`}</span>
            </Link>{' '}
            {!!riskAssessment && (
              <span className={styles.black}>{getRiskAssessmentLabel(riskAssessment)}/</span>
            )}
            SR
            {aot ? <span className={styles.black}>/AOT</span> : ''}
          </div>
          {isGlobalUser && !!actTeams?.length && (
            <div className={styles.textItem}>{`Teams: ${actTeams
              .map(t => t.team.name)
              .join(', ')}`}</div>
          )}
          <div className={styles.textItem}>
            {medicaId ? (
              <>
                <span
                  style={{
                    color: medicaIdColor,
                  }}
                >
                  {medicaId}
                </span>
                <span> / </span>
              </>
            ) : (
              ''
            )}
            {`${gender?.name || ''} / ${
              dateOfBirth ? isoToLocal(dateOfBirth, generalDateDotted) : ''
            }`}
          </div>
          {primaryDiagnosis?.diagnosis.name && (
            <div
              className={styles.textItem}
            >{`Primary Dx: ${primaryDiagnosis?.diagnosis.name}`}</div>
          )}
          {!!clientAddress && <div className={styles.textItem}>{`Address: ${clientAddress}`}</div>}
          {medicaidPlan?.medicaidType && (
            <div className={classNames(styles.textItem, styles.medicaidRow)}>
              <span>Medicaid Type:</span>
              <MedicaidTypeLabel type={medicaidPlan.medicaidType} />
            </div>
          )}
          {isExpansion && (
            <div className={classNames(styles.textItem, styles.medicaidRow)}>
              <span className={styles.ceLabel}>Work / Engagement Hours</span>
              <CeChip client={{ clinicId, clientId }} />
            </div>
          )}
          {onVisitsPage && !!formattedRecertification?.length && (
            <div
              className={styles.textItem}
              style={{
                color: medicaIdColor,
              }}
            >
              <span className={styles.textItem}>Recertification Date:</span>{' '}
              {formattedRecertification}
              {medicaIdRecertificationDate ? getExpiresIn(medicaIdRecertificationDate) : ''}
              {!!true && (
                <>
                  <br />
                  <span className={styles.paperwork}>{getPaperworkStatus(paperworkStatus)}</span>
                </>
              )}
            </div>
          )}
          <StatusHistoryItem item={status} className={styles.clientStatus} withPrefixes />
        </Box>
      )}
    </Box>
  );
};

export default MainInfo;
